import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { chipsDosGrupos } from './kmzConsulta';

function iconeRemoto(url) {
  return /^(https?:|blob:)/i.test(String(url || ''));
}

function Simbolo({ simbolo, cor, iconeUrl }) {
  const fill = cor || '#78716c';
  if (iconeRemoto(iconeUrl) && simbolo !== 'linha' && simbolo !== 'pasta') {
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          flexShrink: 0,
          bgcolor: fill,
          WebkitMask: `url(${iconeUrl}) center / contain no-repeat`,
          mask: `url(${iconeUrl}) center / contain no-repeat`,
        }}
      />
    );
  }
  if (simbolo === 'pasta') {
    return <FolderOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />;
  }
  if (simbolo === 'triangulo') {
    return (
      <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
        <path d="M8 1.6 L14.4 13.6 H1.6 Z" fill={fill} stroke="#111827" strokeWidth="1" />
      </Box>
    );
  }
  if (simbolo === 'quadrado') {
    return (
      <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
        <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1.2" fill={fill} stroke="#111827" strokeWidth="1" />
      </Box>
    );
  }
  if (simbolo === 'linha') {
    return (
      <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
        <line x1="1.5" y1="8" x2="14.5" y2="8" stroke={fill} strokeWidth="3.5" strokeLinecap="round" />
      </Box>
    );
  }
  if (simbolo === 'anel') {
    return (
      <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
        <circle cx="8" cy="8" r="4.4" fill="none" stroke="#111827" strokeWidth="2.4" />
        <circle cx="8" cy="8" r="4.4" fill="none" stroke={fill} strokeWidth="1.4" />
      </Box>
    );
  }
  if (simbolo === 'pino') {
    return (
      <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
        <path d="M8 1.4a3.8 3.8 0 0 0-3.8 3.8c0 2.8 3.8 8.4 3.8 8.4s3.8-5.6 3.8-8.4A3.8 3.8 0 0 0 8 1.4z" fill={fill} />
        <circle cx="8" cy="5.2" r="1.3" fill="#fff" />
      </Box>
    );
  }
  return (
    <Box component="svg" width={16} height={16} viewBox="0 0 16 16" sx={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="5" fill={fill} stroke="#111827" strokeWidth="1.2" />
    </Box>
  );
}

Simbolo.propTypes = {
  simbolo: PropTypes.string,
  cor: PropTypes.string,
  iconeUrl: PropTypes.string,
};

Simbolo.defaultProps = {
  simbolo: 'circulo',
  cor: '#57534e',
  iconeUrl: '',
};

function Olho({ aberto, onClick, label }) {
  return (
    <IconButton
      className="olho-item"
      size="small"
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      sx={{ p: 0.25, flexShrink: 0 }}
    >
      {aberto
        ? <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
        : <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />}
    </IconButton>
  );
}

Olho.propTypes = {
  aberto: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

function folhasDe(no) {
  if (!no || no.folha) return [];
  const proprias = no.travado ? [] : [no.id];
  return proprias.concat((no.filhos || []).filter((filho) => !filho.folha).flatMap(folhasDe));
}

function Linha({ nome, aberto, recolhido, temFilhos, onToggle, onRecolher }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.25}
      sx={{ px: 0.5, py: 0.25, minWidth: 0 }}
    >
      <IconButton
        size="small"
        aria-label={recolhido ? `Expandir ${nome}` : `Recolher ${nome}`}
        disabled={!temFilhos}
        onClick={onRecolher}
        sx={{ p: 0.25 }}
      >
        {recolhido
          ? <ChevronRightIcon sx={{ fontSize: 18 }} />
          : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      <Typography
        variant="body2"
        noWrap
        onClick={temFilhos ? onRecolher : undefined}
        sx={{ flex: 1, minWidth: 0, cursor: temFilhos ? 'pointer' : 'default', fontWeight: 600, color: aberto ? 'text.primary' : 'text.disabled' }}
      >
        {nome}
      </Typography>
      <Olho aberto={aberto} label={aberto ? `Ocultar ${nome}` : `Mostrar ${nome}`} onClick={onToggle} />
    </Stack>
  );
}

Linha.propTypes = {
  nome: PropTypes.string.isRequired,
  aberto: PropTypes.bool.isRequired,
  recolhido: PropTypes.bool.isRequired,
  temFilhos: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRecolher: PropTypes.func.isRequired,
};

function NoPasta({ no, profundidade, fechados, onToggleGrupo, onEnquadrarGrupo }) {
  const folhas = folhasDe(no);
  const abertas = folhas.filter((id) => !fechados[id]).length;
  const aberto = !folhas.length || abertas === folhas.length;
  const travado = Boolean(no.travado) && !folhas.length;
  const filhos = no.filhos || [];
  const [recolhido, setRecolhido] = useState(true);
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={() => onEnquadrarGrupo(no)}
        sx={{
          pr: 0.5,
          py: 0.25,
          pl: 4.5 + profundidade * 1.5,
          minWidth: 0,
          cursor: 'pointer',
          '& .olho-item': { opacity: travado ? 0 : (aberto ? 0 : 1) },
          '&:hover .olho-item': { opacity: travado ? 0 : 1 },
        }}
      >
        {filhos.length ? (
          <IconButton
            size="small"
            aria-label={recolhido ? `Expandir ${no.nome}` : `Recolher ${no.nome}`}
            onClick={(event) => {
              event.stopPropagation();
              setRecolhido((atual) => !atual);
            }}
            sx={{ p: 0.25, ml: -0.5 }}
          >
            {recolhido
              ? <ChevronRightIcon sx={{ fontSize: 16 }} />
              : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        ) : null}
        <Simbolo simbolo={no.simbolo} cor={no.cor} iconeUrl={no.iconeUrl} />
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, minWidth: 0, color: aberto ? 'text.primary' : 'text.disabled' }}
        >
          {no.nome}
        </Typography>
        {travado || no.folha ? null : (
          <Olho
            aberto={aberto}
            label={aberto ? `Ocultar ${no.nome}` : `Mostrar ${no.nome}`}
            onClick={() => onToggleGrupo(folhas, !aberto)}
          />
        )}
      </Stack>
      {!recolhido ? filhos.map((filho) => (
        <NoPasta
          key={filho.id}
          no={filho}
          profundidade={profundidade + 1}
          fechados={fechados}
          onToggleGrupo={onToggleGrupo}
          onEnquadrarGrupo={onEnquadrarGrupo}
        />
      )) : null}
    </>
  );
}

NoPasta.propTypes = {
  no: PropTypes.shape({
    id: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
    travado: PropTypes.bool,
    simbolo: PropTypes.string,
    cor: PropTypes.string,
    iconeUrl: PropTypes.string,
    folha: PropTypes.bool,
    fid: PropTypes.string,
    filhos: PropTypes.array,
  }).isRequired,
  profundidade: PropTypes.number.isRequired,
  fechados: PropTypes.object.isRequired,
  onToggleGrupo: PropTypes.func.isRequired,
  onEnquadrarGrupo: PropTypes.func.isRequired,
};

function ligadoDo(filtro, fechados) {
  return filtro.grupoIds.every((id) => !(fechados && fechados[id]));
}

function FiltroTipos({ filtros, fechados, onToggleGrupo }) {
  const [ancora, setAncora] = useState(null);
  const ocultos = filtros.filter((item) => !ligadoDo(item, fechados)).length;
  const idsDe = (lista) => lista.flatMap((item) => item.grupoIds);
  return (
    <>
      <Badge color="primary" badgeContent={ocultos || null} overlap="circular">
        <IconButton
          size="small"
          aria-label="Filtrar o que aparece no mapa"
          aria-expanded={Boolean(ancora)}
          onClick={(event) => setAncora(event.currentTarget)}
          color={ocultos ? 'primary' : 'default'}
        >
          <FilterListOutlinedIcon fontSize="small" />
        </IconButton>
      </Badge>
      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack sx={{ width: 240, py: 1, px: 1.5 }} spacing={0.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Mostrar no mapa</Typography>
            <Typography
              component="button"
              type="button"
              variant="caption"
              onClick={() => onToggleGrupo(idsDe(filtros), ocultos > 0)}
              sx={{
                border: 0,
                p: 0,
                bgcolor: 'transparent',
                color: 'primary.main',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {ocultos ? 'Mostrar tudo' : 'Ocultar tudo'}
            </Typography>
          </Stack>
          {filtros.map((item) => {
            const ligado = ligadoDo(item, fechados);
            return (
              <FormControlLabel
                key={item.id}
                sx={{
                  mx: 0,
                  my: 0,
                  '& .MuiCheckbox-root': { py: 0.25 },
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                }}
                control={(
                  <Checkbox
                    size="small"
                    checked={ligado}
                    onChange={() => onToggleGrupo(item.grupoIds, !ligado)}
                  />
                )}
                label={item.nome}
              />
            );
          })}
        </Stack>
      </Popover>
    </>
  );
}

FiltroTipos.propTypes = {
  filtros: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
    grupoIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  fechados: PropTypes.object.isRequired,
  onToggleGrupo: PropTypes.func.isRequired,
};

function Busca({
  termo,
  onTermo,
  indice,
  total,
  estadoBusca,
  onPasso,
  isolado,
  onIsolar,
  onMostrarTudo,
  filtros,
  fechados,
  onToggleGrupo,
}) {
  const comAcerto = estadoBusca === 'ok';
  return (
    <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar código"
          value={termo}
          onChange={(event) => onTermo(event.target.value)}
          inputProps={{ 'aria-label': 'Buscar código' }}
        />
        {filtros.length ? (
          <FiltroTipos filtros={filtros} fechados={fechados} onToggleGrupo={onToggleGrupo} />
        ) : null}
      </Stack>
      {comAcerto ? (
        <Box sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <IconButton size="small" aria-label="Acerto anterior" onClick={() => onPasso(-1)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
              {`${indice + 1}/${total}`}
            </Typography>
            <IconButton size="small" aria-label="Próximo acerto" onClick={() => onPasso(1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={isolado ? onMostrarTudo : onIsolar}
            sx={{
              border: 0,
              p: 0,
              bgcolor: 'transparent',
              color: 'primary.main',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            {isolado ? 'Mostrar tudo' : 'Mostrar apenas estes'}
          </Typography>
        </Box>
      ) : null}
      {estadoBusca === 'vazio' ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 0.5 }}>
          Nenhum resultado
        </Typography>
      ) : null}
    </Box>
  );
}

Busca.propTypes = {
  termo: PropTypes.string.isRequired,
  onTermo: PropTypes.func.isRequired,
  indice: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  estadoBusca: PropTypes.string.isRequired,
  onPasso: PropTypes.func.isRequired,
  isolado: PropTypes.bool.isRequired,
  onIsolar: PropTypes.func.isRequired,
  onMostrarTudo: PropTypes.func.isRequired,
  filtros: PropTypes.array.isRequired,
  fechados: PropTypes.object.isRequired,
  onToggleGrupo: PropTypes.func.isRequired,
};

const KmzCamadasPainel = ({
  grupo,
  linhas,
  kmzNome,
  rotaNome,
  cru,
  gerado,
  rota,
  onToggle,
  grupos,
  fechados,
  onToggleGrupo,
  onEnquadrarGrupo,
  termo,
  onTermo,
  indice,
  total,
  estadoBusca,
  onPasso,
  isolado,
  onIsolar,
  onMostrarTudo,
}) => {
  const itens = Array.isArray(linhas)
    ? linhas
    : [
      { chave: 'cru', nome: kmzNome || 'KMZ matriz', aberto: cru },
      ...(rotaNome ? [{ chave: 'rota', nome: rotaNome, aberto: rota }] : []),
      { chave: 'gerado', nome: 'Trecho gerado', aberto: gerado },
    ];
  const consulta = Boolean(onTermo);
  const filtros = consulta ? chipsDosGrupos(grupos) : [];
  const [abertos, setAbertos] = useState({});
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {consulta ? (
        <>
          <Busca
            termo={termo}
            onTermo={onTermo}
            indice={indice}
            total={total}
            estadoBusca={estadoBusca}
            onPasso={onPasso}
            isolado={isolado}
            onIsolar={onIsolar}
            onMostrarTudo={onMostrarTudo}
            filtros={filtros}
            fechados={fechados || {}}
            onToggleGrupo={onToggleGrupo}
          />
        </>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 1 }}>
        <Typography variant="subtitle2" sx={{ px: 1.5, pb: 0.5 }}>
          Arquivos no mapa
        </Typography>
        {grupo && (
          <Typography variant="body2" sx={{ px: 1.5, py: 0.5, fontWeight: 600 }}>
            {grupo}
          </Typography>
        )}
        {itens.map((linha) => {
          const nos = (grupos && grupos[linha.chave]) || [];
          return (
            <Box key={linha.chave}>
              <Linha
                nome={linha.nome}
                aberto={Boolean(linha.aberto)}
                recolhido={!abertos[linha.chave]}
                temFilhos={Boolean(consulta && nos.length)}
                onToggle={() => onToggle(linha.chave)}
                onRecolher={() => setAbertos((atual) => ({ ...atual, [linha.chave]: !atual[linha.chave] }))}
              />
              {consulta && abertos[linha.chave] ? nos.map((no) => (
                <NoPasta
                  key={no.id}
                  no={no}
                  profundidade={0}
                  fechados={fechados || {}}
                  onToggleGrupo={onToggleGrupo}
                  onEnquadrarGrupo={onEnquadrarGrupo}
                />
              )) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

KmzCamadasPainel.propTypes = {
  grupo: PropTypes.string,
  linhas: PropTypes.arrayOf(PropTypes.shape({
    chave: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
    aberto: PropTypes.bool,
  })),
  kmzNome: PropTypes.string,
  rotaNome: PropTypes.string,
  cru: PropTypes.bool,
  gerado: PropTypes.bool,
  rota: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  grupos: PropTypes.object,
  fechados: PropTypes.object,
  onToggleGrupo: PropTypes.func,
  onEnquadrarGrupo: PropTypes.func,
  termo: PropTypes.string,
  onTermo: PropTypes.func,
  indice: PropTypes.number,
  total: PropTypes.number,
  estadoBusca: PropTypes.string,
  onPasso: PropTypes.func,
  isolado: PropTypes.bool,
  onIsolar: PropTypes.func,
  onMostrarTudo: PropTypes.func,
  tipos: PropTypes.object,
  onToggleTipo: PropTypes.func,
};

KmzCamadasPainel.defaultProps = {
  grupo: '',
  linhas: null,
  kmzNome: '',
  rotaNome: '',
  cru: false,
  gerado: false,
  rota: false,
  grupos: null,
  fechados: {},
  onToggleGrupo: () => {},
  onEnquadrarGrupo: () => {},
  termo: '',
  onTermo: null,
  indice: 0,
  total: 0,
  estadoBusca: 'curto',
  onPasso: () => {},
  isolado: false,
  onIsolar: () => {},
  onMostrarTudo: () => {},
  tipos: null,
  onToggleTipo: () => {},
};

export default KmzCamadasPainel;
